import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/**
 * BackButton — small, secondary control placed near the top-left of any
 * authenticated LifeGuide screen (Home excluded — handled by the caller).
 *
 * Behavior:
 *  - Uses browser/history back when there's a valid in-app prior page.
 *  - Falls back to `/` when the current page is the first in-app entry, so
 *    the user is never sent back into external history (e.g. a bookmark, a
 *    deep-link, or an SSO/gift-claim redirect).
 *
 * We detect "has valid in-app history" via React Router's `location.key`:
 * the very first entry to the app is keyed `"default"`; any subsequent
 * push/replace inside the SPA has a random key. If the current key is not
 * `"default"`, we know there's at least one prior SPA frame — safe to go back.
 */
export default function BackButton({ testid = "back-button" }) {
  const navigate = useNavigate();
  const location = useLocation();

  const goBack = () => {
    const hasSpaHistory = location.key && location.key !== "default";
    if (hasSpaHistory) {
      navigate(-1);
    } else {
      navigate("/", { replace: true });
    }
  };

  return (
    <button
      type="button"
      onClick={goBack}
      data-testid={testid}
      className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-[#2E4531] hover:text-[#1F2F22] rounded-full pl-1.5 pr-3 py-1.5 hover:bg-[#E1ECDD] transition"
      aria-label="Go back"
    >
      <ArrowLeft size={15} strokeWidth={2.4} />
      Back
    </button>
  );
}
